import { GetGenericItemBySectionVariables } from '@marty-js/api-sdk/__generated__/GetGenericItemBySection';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetGenericItemBySection } from '@marty-js/api-sdk/services/genericItemBySection';
import { DataSheetFilter } from '@marty-js/api-sdk/__generated__/globalTypes';
import { GenericItem, Section } from '@marty-js/api-sdk/types';
import type { PluginFilterAggs, PluginGenericItemByQueryConfig } from '../page-content/template/types';

export const useFilterGenericItemBySection = (
  section: Section,
  filterAggs: PluginFilterAggs,
  flux: PluginGenericItemByQueryConfig,
) => {
  const size = flux?.info?.size ?? 10;
  const [filters, setFilters] = useState<DataSheetFilter[]>([]);
  const [currentSort, setCurrentSort] = useState<string>(filterAggs.currentSort);
  const [genericItems, setGenericItems] = useState<GenericItem[]>(flux?.genericItemList);
  const [total, setTotal] = useState<number>(flux.info?.total);

  const [isDefaultFlux, setDefaultFlux] = useState<boolean>(true);

  const variables: GetGenericItemBySectionVariables = useMemo(() => {
    return {
      sectionId: section.id,
      from: 0,
      size,
      sort: currentSort,
      dataSheetFilters: filters,
    };
  }, [section.id, size, filters, currentSort]);

  const { fetchMoreData, fetchData, result, loading, called } = useGetGenericItemBySection({ variables });

  const callbackPostSort = useCallback(
    (sort: string) => {
      setGenericItems([]);
      fetchData({
        variables: {
          ...variables,
          sort,
        },
      });

      setDefaultFlux(false);
      setCurrentSort(sort);
    },
    [setCurrentSort, setDefaultFlux, fetchData, variables],
  );

  const callbackPostFilters = useCallback(
    (dataSheetFilters: DataSheetFilter[]) => {
      fetchData({
        variables: {
          ...variables,
          dataSheetFilters,
        },
      });
      // fluxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

      setGenericItems([]);
      setDefaultFlux(false);
      setFilters(dataSheetFilters);
    },
    [setFilters, setDefaultFlux, fetchData, variables],
  );

  useEffect(() => {
    if (!loading) {
      if (result) {
        if (isDefaultFlux) {
          setGenericItems([...flux?.genericItemList, ...result.genericItemBySection.genericItems]);
          setTotal(flux?.info?.total);
        } else {
          setGenericItems(result.genericItemBySection.genericItems);
          setTotal(result.genericItemBySection.total);
        }
      }
    }
  }, [setGenericItems, result, loading, isDefaultFlux, flux, section.url]);

  const fetchMore = useCallback(
    (from) => {
      if (isDefaultFlux || result) {
        if (called) {
          fetchMoreData({ from });
        } else if (!loading) {
          fetchData({ variables: { ...variables, from } });
        }
      }
    },
    [isDefaultFlux, result, called, loading, fetchMoreData, fetchData, variables],
  );

  const callbackReset = useCallback(() => {
    setGenericItems(flux.genericItemList);
    setFilters([]);
    setTotal(flux?.info?.total);
    setDefaultFlux(true);
    setCurrentSort(filterAggs.currentSort);
    fetchData({ reset: true });
  }, [flux.genericItemList, flux?.info?.total, filterAggs.currentSort, fetchData]);

  const resultsOrderProps = useMemo(
    () => ({
      resultNum: total,
      sortOptions: filterAggs.allSortableSlugs,
      currentSort,
    }),
    [total, currentSort, filterAggs.allSortableSlugs],
  );

  return { fetchMore, genericItems, callbackPostFilters, callbackPostSort, resultsOrderProps, loading, callbackReset };
};
